import React, { useEffect, useMemo, useRef, useState } from "react";
import { Auth } from "aws-amplify";
import { useHistory, useLocation } from "react-router-dom";
import "../../auth.css";

const AUTH_STATUS_BASE = `https://${process.env.REACT_APP_ACCOUNT_API_URL}`;
const MFA_TOTP_RESET_START_URL = process.env.REACT_APP_MFA_TOTP_RESET_START_URL;
const LS_KEY = "ph_prefill_email";

function rememberEmail(key, value) {
  try {
    localStorage.setItem(key, value || "");
  } catch {}
}

function readEmailFromLocation(location) {
  const searchParams = new URLSearchParams(location.search);
  const queryEmail = (searchParams.get("email") || "").trim();
  const storedEmail =
    (!queryEmail &&
      typeof localStorage !== "undefined" &&
      localStorage.getItem(LS_KEY)) ||
    "";

  return queryEmail || storedEmail;
}

function readRecoveryTokenFromLocation(location) {
  const searchParams = new URLSearchParams(location.search);
  return (searchParams.get("token") || "").trim();
}

function parseIsoUtc(value) {
  if (!value || typeof value !== "string" || !value.trim()) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value) {
  const parsed = parseIsoUtc(value);
  return parsed ? parsed.toLocaleString() : value || "—";
}

async function getResetStateFromBackend(username, recoveryToken) {
  const params = new URLSearchParams();
  params.set("username", username.trim());
  params.set("recoveryToken", recoveryToken.trim());

  const response = await fetch(
    `${AUTH_STATUS_BASE}/auth/status?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok || !data || typeof data !== "object") {
    throw new Error("We couldn’t confirm this authenticator reset request.");
  }

  const status = String(data.mfaRecoveryStatus || "").trim().toLowerCase();
  const approvedAtRaw = String(data.mfaRecoveryApprovedAt || "");
  const expiresAtRaw = String(data.mfaRecoveryExpiresAt || "");
  const expiresAt = parseIsoUtc(expiresAtRaw);
  const tokenRequired = !!data.mfaRecoveryTokenRequired;
  const tokenValid = !!data.mfaRecoveryTokenValid;
  const linkInvalid = !!data.mfaRecoveryLinkInvalid;
  const linkExpired = !!data.mfaRecoveryLinkExpired;
  const linkAlreadyUsed = !!data.mfaRecoveryLinkAlreadyUsed;

  return {
    status,
    approvedAtRaw,
    expiresAtRaw,
    isApproved:
      status === "approved" &&
      !!expiresAt &&
      expiresAt.getTime() > Date.now() &&
      (!tokenRequired || tokenValid) &&
      !linkInvalid &&
      !linkExpired &&
      !linkAlreadyUsed,
    isExpired:
      linkExpired ||
      status === "expired" ||
      (status === "approved" &&
        !!expiresAt &&
        expiresAt.getTime() <= Date.now()),
    isCompleted: status === "completed" || status === "reset_started",
    hasSoftwareTokenMfa: !!data.hasSoftwareTokenMfa,
    mfaRecoveryTokenRequired: tokenRequired,
    mfaRecoveryLinkInvalid: linkInvalid,
    mfaRecoveryLinkExpired: linkExpired,
    mfaRecoveryLinkAlreadyUsed: linkAlreadyUsed,
  };
}

async function startTotpReset(username, password, recoveryToken) {
  const resetUrl = (MFA_TOTP_RESET_START_URL || "").trim();

  if (!resetUrl) {
    throw new Error("REACT_APP_MFA_TOTP_RESET_START_URL is not configured.");
  }

  const response = await fetch(resetUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      username,
      password,
      recoveryToken,
    }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      data && typeof data.message === "string"
        ? data.message
        : "Authenticator app reset failed.";
    throw new Error(message);
  }

  if (!data || typeof data !== "object") {
    throw new Error("Authenticator app reset returned an invalid response.");
  }

  return data;
}

export default function MfaTotpReset() {
  const history = useHistory();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [password, setPassword] = useState("");

  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resetState, setResetState] = useState(null);

  const passwordRef = useRef(null);

  useEffect(() => {
    const resolvedEmail = readEmailFromLocation(location);
    const resolvedRecoveryToken = readRecoveryTokenFromLocation(location);

    setEmail(resolvedEmail);
    setRecoveryToken(resolvedRecoveryToken);

    let mounted = true;

    async function loadResetState() {
      if (!resolvedEmail) {
        if (mounted) {
          setChecking(false);
          setError("Email is missing. Please go back to sign in.");
        }
        return;
      }

      if (!resolvedRecoveryToken) {
        if (mounted) {
          setChecking(false);
          setError(
            "This recovery link is missing its token. Please contact support or a Global Admin for a new recovery link."
          );
        }
        return;
      }

      try {
        const state = await getResetStateFromBackend(
          resolvedEmail,
          resolvedRecoveryToken
        );

        if (!mounted) return;

        setResetState(state);

        if (state.mfaRecoveryLinkAlreadyUsed || state.isCompleted) {
          setError(
            "This recovery link has already been used. Please contact support or a Global Admin for a new recovery link."
          );
        } else if (state.mfaRecoveryLinkExpired || state.isExpired) {
          setError(
            "This recovery link has expired. Please contact support or a Global Admin for a new recovery link."
          );
        } else if (state.mfaRecoveryLinkInvalid) {
          setError(
            "This recovery link is no longer valid. Please contact support or a Global Admin for a new recovery link."
          );
        } else if (!state.isApproved) {
          setError(
            "Authenticator reset is not approved yet. Please contact support."
          );
        } else if (!state.hasSoftwareTokenMfa) {
          setSuccess(
            "Authenticator app MFA is already reset. Please sign in to set up a new authenticator app."
          );
        }
      } catch (loadError) {
        if (mounted) {
          setError(
            loadError?.message ||
              "We couldn’t confirm this authenticator reset request."
          );
        }
      } finally {
        if (mounted) {
          setChecking(false);
        }
      }
    }

    loadResetState();

    return () => {
      mounted = false;
    };
  }, [location]);

  useEffect(() => {
    if (!checking && !error && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [checking, error]);

  const isReadyToReset = useMemo(() => {
    return (
      !!email &&
      !!recoveryToken &&
      !!resetState?.isApproved &&
      !resetState?.isExpired &&
      !resetState?.isCompleted &&
      !resetState?.mfaRecoveryLinkInvalid &&
      !resetState?.mfaRecoveryLinkExpired &&
      !resetState?.mfaRecoveryLinkAlreadyUsed &&
      !!resetState?.hasSoftwareTokenMfa
    );
  }, [email, recoveryToken, resetState]);

  async function submitTotpReset(event) {
    event.preventDefault();

    const normalizedEmail = email.trim();
    const normalizedPassword = password;
    const normalizedRecoveryToken = recoveryToken.trim();

    if (!normalizedEmail) {
      setError("Email is missing. Please go back to sign in.");
      return;
    }

    if (!normalizedPassword) {
      setError("Please enter your password.");
      return;
    }

    if (!normalizedRecoveryToken) {
      setError(
        "This recovery link is missing its token. Please contact support or a Global Admin for a new recovery link."
      );
      return;
    }

    setBusy(true);
    setError("");
    setSuccess("");
    rememberEmail(LS_KEY, normalizedEmail);

    try {
      const result = await startTotpReset(
        normalizedEmail,
        normalizedPassword,
        normalizedRecoveryToken
      );

      setSuccess(
        result.message ||
          "Existing authenticator app has been reset. Please sign in again to set up a new authenticator app."
      );
      setPassword("");
      setResetState((previousState) => {
        if (!previousState) return previousState;

        return {
          ...previousState,
          status: result.mfaRecoveryStatus || "reset_started",
          isApproved: false,
          isCompleted: true,
          hasSoftwareTokenMfa: false,
          mfaRecoveryLinkAlreadyUsed: true,
        };
      });
    } catch (resetError) {
      console.error("Error resetting authenticator app", resetError);

      const message =
        resetError?.message ||
        "We couldn’t reset your authenticator app. Please try again.";

      setError(
        /invalid username or password/i.test(message)
          ? "Invalid email or password."
          : message
      );
    } finally {
      setBusy(false);
    }
  }

  function goBackToSignIn() {
    history.push(`/signin?email=${encodeURIComponent(email)}`);
  }

  async function goToNewTotpSetupSignIn() {
    setBusy(true);
    setError("");

    try {
      await Auth.signOut();
      history.push(
        `/signin?email=${encodeURIComponent(email)}&totpReset=1`
      );
    } catch (signOutError) {
      console.error(
        "Error clearing the existing Cognito session before TOTP setup",
        signOutError
      );
      setError(
        "We couldn’t clear the existing session. Please close this browser window and open the recovery link in an Incognito window."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ph-auth-wrap">
      <div className="ph-auth-card">
        <h1 className="ph-auth-title">Reset authenticator app</h1>

        {checking ? (
          <p className="ph-hint">Checking authenticator reset request…</p>
        ) : (
          <>
            <p className="ph-hint">
              Use this page only if support has approved resetting your
              authenticator app.
            </p>

            <p className="ph-hint" style={{ marginBottom: 12 }}>
              <b>Email:</b> {email || "—"}
            </p>

            {resetState && (
              <div style={{ marginBottom: 16 }}>
                <p className="ph-hint" style={{ marginBottom: 4 }}>
                  <b>Status:</b> {resetState.status || "—"}
                </p>
                <p className="ph-hint" style={{ marginBottom: 4 }}>
                  <b>Approved At:</b>{" "}
                  {formatDateTime(resetState.approvedAtRaw)}
                </p>
                <p className="ph-hint" style={{ marginBottom: 4 }}>
                  <b>Expires At:</b>{" "}
                  {formatDateTime(resetState.expiresAtRaw)}
                </p>
                <p className="ph-hint" style={{ marginBottom: 0 }}>
                  <b>Authenticator app currently enabled:</b>{" "}
                  {resetState.hasSoftwareTokenMfa ? "Yes" : "No"}
                </p>
              </div>
            )}

            {isReadyToReset && (
              <form onSubmit={submitTotpReset}>
                <p className="ph-hint" style={{ marginBottom: 12 }}>
                  Enter your password to verify your identity. Your old
                  authenticator app will be disabled, and you will then sign in
                  again to scan a new QR code.
                </p>

                <label className="ph-label" htmlFor="ph-totp-reset-password">
                  Password
                </label>
                <input
                  id="ph-totp-reset-password"
                  className="ph-field"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  ref={passwordRef}
                  disabled={busy}
                />

                {error && (
                  <div className="ph-error" role="alert">
                    {error}
                  </div>
                )}

                <button
                  className="ph-button"
                  type="submit"
                  disabled={busy || !password}
                >
                  {busy ? "Resetting…" : "Reset authenticator app"}
                </button>
              </form>
            )}

            {!isReadyToReset && error && (
              <div className="ph-error" role="alert">
                {error}
              </div>
            )}

            {!isReadyToReset && success && (
              <div
                className="ph-hint"
                role="status"
                style={{ color: "#166534" }}
              >
                {success}
              </div>
            )}

            {success && (
              <button
                className="ph-button"
                type="button"
                onClick={goToNewTotpSetupSignIn}
                disabled={busy}
                style={{ marginTop: 12 }}
              >
                {busy
                  ? "Preparing sign in…"
                  : "Sign in and set up new authenticator app"}
              </button>
            )}

            <p className="ph-hint" style={{ marginTop: 16 }}>
              <a
                className="ph-link"
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  goBackToSignIn();
                }}
              >
                Go back to sign in
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}